import { useState } from "react";
import { Download, FileDown, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, download, errMsg } from "@/lib/api";
import { Btn, Pick } from "@/components/app/bits";
import { useSystems } from "./ChecksheetsTab";

const FIELDS = {
  tags: [["tag_no", "Tag number", true, ["tag number", "tag no", "tag", "tagno", "tag_no"]], ["description", "Tag description", false, ["tag description", "description", "desc"]],
    ["system_code", "System", false, ["system", "system no", "system code", "system number"]], ["system_name", "System description", false, ["system description", "system name"]],
    ["subsystem_code", "Subsystem", false, ["subsystem", "sub-system", "subsystem no", "subsystem code"]], ["subsystem_name", "Subsystem description", false, ["subsystem description", "subsystem name"]],
    ["discipline", "Discipline", false, ["discipline", "disc"]], ["equipment_type", "Equipment type", false, ["equipment type", "equipment class", "type", "class", "tag type"]]],
  checksheet_status: [["tag_no", "Tag number", true, ["tag number", "tag no", "tag"]], ["template_code", "Checksheet code", true, ["checksheet code", "itr", "itr code", "form", "sheet code", "checksheet"]],
    ["status", "Status", true, ["status"]], ["completed_date", "Completed date", false, ["completed date", "completion date", "date completed", "signed date"]], ["signed_by", "Signed by", false, ["signed by", "completed by"]]],
  punch: [["punch_no", "Punch number", false, ["punch number", "punch no", "punch item", "number"]], ["tag_no", "Tag number", true, ["tag number", "tag no", "tag"]],
    ["category", "Category", false, ["category", "cat", "priority"]], ["description", "Description", true, ["description", "punch description"]], ["status", "Status", false, ["status"]],
    ["owner", "Action by", false, ["action by", "owner", "responsible"]], ["due_date", "Due date", false, ["due date", "target date", "required by"]]],
};
const KINDS = [["tags", "Tag register"], ["checksheet_status", "Checksheet status"], ["punch", "Punch list"]];
const norm = (s) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

function Importer({ project }) {
  const [kind, setKind] = useState("tags");
  const [prev, setPrev] = useState(null);
  const [mapping, setMapping] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/projects/${project.id}/import/preview`, fd);
      setPrev(r.data);
      const guess = {};
      FIELDS[kind].forEach(([key, , , syn]) => { guess[key] = r.data.columns.find((c) => syn.some((s) => norm(s) === norm(c))) || ""; });
      setMapping(guess);
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  const commit = async () => {
    setBusy(true);
    try { const r = await api.post(`/projects/${project.id}/import/commit`, { token: prev.token, kind, mapping }); setResult(r.data); setPrev(null); toast.success("Import complete"); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const missing = FIELDS[kind].filter(([k, , req]) => req && !mapping[k]);

  return (
    <div className="panel p-5 space-y-4" data-testid="importer">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div><p className="font-semibold text-slate-100">Import CSV or Excel</p><p className="text-sm text-slate-400">Tag imports generate systems, subsystems, tags and checksheets from each equipment type.</p></div>
        <div className="flex gap-2">
          <Pick testid="import-kind" value={kind} onChange={(v) => { setKind(v || "tags"); setPrev(null); setResult(null); }} options={KINDS} className="w-48" />
          <label className="btn-accent px-4 py-2 text-sm flex items-center gap-2 cursor-pointer" data-testid="import-upload-btn">
            <Upload className="w-4 h-4" />{busy ? "Working…" : "Choose file"}<input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={upload} disabled={busy} data-testid="import-file-input" />
          </label>
        </div>
      </div>
      {prev && (
        <div className="space-y-4" data-testid="import-mapping">
          <p className="text-sm text-slate-300">{prev.rows} rows found. Map your columns:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {FIELDS[kind].map(([key, label, req]) => (
              <div key={key} className="flex items-center gap-3">
                <span className="w-44 text-sm text-slate-300">{label}{req && <span className="text-amber-500"> *</span>}</span>
                <Pick testid={`map-${key}`} value={mapping[key]} onChange={(v) => setMapping({ ...mapping, [key]: v })} allLabel="(not mapped)" options={prev.columns} className="flex-1" />
              </div>
            ))}
          </div>
          <div className="overflow-x-auto border border-[#1E2D4A]">
            <table className="text-xs w-full"><thead><tr>{prev.columns.map((c) => <th key={c} className="px-2 py-1.5 text-left text-slate-500 font-mono">{c}</th>)}</tr></thead>
              <tbody>{prev.sample.map((r, i) => <tr key={i} className="border-t border-[#1E2D4A]">{prev.columns.map((c) => <td key={c} className="px-2 py-1.5 text-slate-300 whitespace-nowrap">{r[c]}</td>)}</tr>)}</tbody></table>
          </div>
          <Btn onClick={commit} disabled={busy || missing.length > 0} data-testid="import-commit-btn">Import {prev.rows} rows</Btn>
        </div>
      )}
      {result && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm space-y-1" data-testid="import-result">
          {Object.entries(result).filter(([k]) => k !== "equipment_types_without_templates").map(([k, v]) => <p key={k} className="text-slate-300"><span className="num text-emerald-400 mr-2">{v}</span>{k.replace(/_/g, " ")}</p>)}
          {result.equipment_types_without_templates?.length > 0 && (
            <p className="text-amber-400">No checksheet templates yet for: {result.equipment_types_without_templates.map((e) => `${e.name} (${e.tags})`).join(", ")}. Assign templates in Settings.</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function DataTab({ project }) {
  const { can } = useAuth();
  const systems = useSystems(project.id);
  const [sys, setSys] = useState("");
  const [busy, setBusy] = useState(false);
  const exp = (kind, fmt) => download(`/projects/${project.id}/export/${kind}?fmt=${fmt}`, `${project.code}_${kind}.${fmt}`).catch((e) => toast.error(errMsg(e)));
  const dossier = async () => {
    const s = systems.find((x) => x.id === sys);
    setBusy(true);
    try { await download(`/systems/${sys}/dossier`, `Dossier_${s.code}.pdf`); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-4" data-testid="data-tab">
      {can("Manager") && <Importer project={project} />}
      <div className="panel p-5 space-y-3" data-testid="exporter">
        <p className="font-semibold text-slate-100">Export</p>
        <p className="text-sm text-slate-400">Column layouts follow common completions system formats, so they can be loaded into a client's CMS.</p>
        <div className="grid md:grid-cols-3 gap-3">
          {KINDS.map(([k, label]) => {
            const kk = k === "checksheet_status" ? "checksheets" : k;
            return (
              <div key={k} className="border border-[#1E2D4A] p-3 flex items-center justify-between">
                <span className="text-sm text-slate-200">{label}</span>
                <span className="flex gap-1">
                  <Btn variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => exp(kk, "xlsx")} data-testid={`export-${kk}-xlsx`}><Download className="w-3 h-3" />XLSX</Btn>
                  <Btn variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => exp(kk, "csv")} data-testid={`export-${kk}-csv`}>CSV</Btn>
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="panel p-5 space-y-3" data-testid="dossier-export">
        <p className="font-semibold text-slate-100">Handover dossier</p>
        <p className="text-sm text-slate-400">PDF per system: completed checksheets, closed punch items, photos and signatures.</p>
        <div className="flex flex-col md:flex-row gap-2">
          <Pick testid="dossier-system-select" value={sys} onChange={setSys} placeholder="Choose system" options={systems.map((s) => [s.id, `${s.code} ${s.name}`])} className="md:w-80" />
          <Btn onClick={dossier} disabled={!sys || busy} data-testid="dossier-download-btn"><FileDown className="w-4 h-4" />{busy ? "Building PDF…" : "Download PDF"}</Btn>
        </div>
      </div>
    </div>
  );
}
